import { createContext, useContext, useEffect, useState } from "react";
import { currentApiUrl } from "@guestflow/shared/env";

interface User {
  id: string;
  email: string;
  name: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const apiUrl = currentApiUrl();

  useEffect(() => {
    fetch(`${apiUrl}/api/me`, { credentials: 'include' })
      .then((r) => r.json())
      .then((data) => {
        setUser(data.user);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [apiUrl]);

  const apiFetch = (path: string, options?: RequestInit) => {
    return fetch(`${apiUrl}${path}`, { ...options, credentials: 'include' });
  };

  const signIn = async (email: string, password: string) => {
    const res = await fetch(`${apiUrl}/auth/sign-in/email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    const result = await res.json();
    if (result.user) setUser(result.user);
  };

  const signUp = async (email: string, password: string, name: string) => {
    const res = await fetch(`${apiUrl}/auth/sign-up/email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: 'include',
      body: JSON.stringify({ email, password, name }),
    });
    const result = await res.json();
    if (result.user) setUser(result.user);
  };

  const signOut = async () => {
    await fetch(`${apiUrl}/auth/sign-out`, { method: "POST", credentials: 'include' });
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
